import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { AuthContext } from "./AuthProvider";
import { getSearchNearbyClubs } from "../Api/api";
import { CgSpinner } from "react-icons/cg";
import { generateGameType } from "../Utilities/utils";
import { useTranslation } from "react-i18next";

export const WidgetCreator = () => {
    const { t } = useTranslation();
    const currentUser = useContext(AuthContext);
    const [loadingClubs, setLoadingClubs] = useState<boolean>(false);
    const [clubs, setClubs] = useState<any>([]);
    const [searchText, setSearchText] = useState(""); // 🔍 For search input
    const [selectedClubs, setSelectedClubs] = useState<any[]>([]);
    const [selectedStyle, setSelectedStyle] = useState<string>("tabs");
    const [color, setColor] = useState({
        blueBgColor: "bg-secondary",
        blueBorderColor: "border-secondary",
        blueTextColor: "text-secondary",
        blueHoverTextColor: "hover:text-secondary",
        blueHoverColor: "hover:bg-secondary",
        greenBgColor: "bg-primary",
        greenBorderColor: "border-primary",
        greenHoverColor: "hover:bg-primary",
        greenTextColor: "text-primary",
        greenOutlineColor: "outline-primary",
        greenfocusColor: "focus-primary",
        grayBgColor: "bg-[#9CA3AF]",
        grayBorderColor: "border-[#9CA3AF]",
        garyTextColor: "text-[#9CA3AF]",
        bgColor: "bg-[#9CA3AF]"
    });

    const [generatedHTML, setGeneratedHTML] = useState("");
    const sports = ["padel", "tennis", "squash", "badminton", "pickleball"];
    const [searchState, setSearchState] = useState(false);
    const [baseHTML, setBaseHTML] = useState<string>("");
    const [generatedBlob, setGeneratedBlob] = useState<Blob | null>(null);

    useEffect(() => {
        const fetchHtml = async () => {
            const res = await fetch("/widgets/index.html");
            const htmlText = await res.text();
            setBaseHTML(htmlText);
        };

        fetchHtml();
    }, []);

    const updateHTMLPreview = () => {
        if (!baseHTML) return;

        const formattedClubs = selectedClubs.map(({ _id, ...rest }) => ({
            id: _id,
            ...rest
        }));

        const updatedHtml = baseHTML
            .replace("{{CLUBS}}", JSON.stringify(formattedClubs))
            .replace("{{STYLE}}", selectedStyle)
            .replace("{{COLOR}}", JSON.stringify(color));

        const blob = new Blob([updatedHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const responsiveIframe = `
        <style>
            .responsive-iframe {
                width: 100%;
                height: 82vh;
                border: none;
            }

            @media (min-width: 1024px) and (max-width: 1279px) {
                .responsive-iframe {
                    height: 70vh;
                }
            }
        </style>
        <iframe src="${url}" class="responsive-iframe"></iframe>
    `;
        setGeneratedHTML(responsiveIframe);
        setGeneratedBlob(blob);
    };

    useEffect(() => {
        updateHTMLPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClubs, selectedStyle, color, baseHTML]);

    const handleColorChange = (key: string, value: string) => {
        const tailwindClass = value.startsWith("#")
            ? key.includes("Hover")
                ? `${
                      key.includes("Text") ? "hover:text-[" : "hover:bg-["
                  }${value}]`
                : key.includes("Text")
                ? `text-[${value}]`
                : key.includes("Border")
                ? `border-[${value}]`
                : key.includes("Outline")
                ? `outline-[${value}]`
                : key.includes("focus")
                ? `focus:[${value}]`
                : `bg-[${value}]`
            : value; // fallback if a named class like "bg-secondary"

        setColor((prev) => ({
            ...prev,
            [key]: tailwindClass
        }));
    };

    const searchNearByClubs = async (clubName: string, date: any) => {
        setLoadingClubs(true);
        setClubs([]);

        let data = {
            radius: 50,
            latitude: 78.9012,
            longitude: 12.3456,
            sports,
            clubName: encodeURIComponent(clubName),
            selectedSport: [],
            date,
            currentUser
        };

        const defaultResult = await getSearchNearbyClubs(data);
        setLoadingClubs(false);
        if (
            defaultResult?.data?.searched_clubs &&
            defaultResult.data.searched_clubs.length > 0
        ) {
            setClubs(
                defaultResult.data.searched_clubs.filter(
                    (club: any) => club.permissions.mobile_booking === true
                )
            );
        }
        setSearchState(true);
    };

    const handleExplore = () => {
        if (searchText.trim()) {
            searchNearByClubs(searchText.trim(), moment().format("YYYY/MM/DD"));
        }
    };

    const toggleClub = (club: { _id: string; name: string }) => {
        setSelectedClubs((prev: any) => {
            const exists = prev.some((c: any) => c._id === club._id);
            if (exists) {
                return prev.filter((c: any) => c._id !== club._id);
            } else {
                return [...prev, { _id: club._id, name: club.name }];
            }
        });
    };

    const formatColorLabel = (key: string): string => {
    const prefixMap: Record<string, string> = {
        blue: "Primary",
        green: "Secondary",
        gray: "Tertiary",
        gary: "Tertiary",
        bg: "Main",
    };

    const suffixMap: Record<string, string> = {
        BgColor: "Background",
        BorderColor: "Border",
        HoverTextColor: "Hover Text",
        TextColor: "Text",
        HoverColor: "Hover Background",
        OutlineColor: "Outline",
        focusColor: "Focus Background",
        Color: "Background"
    };

    const match = key.match(/^(blue|green|gray|gary|bg)?(.*)$/i);
    if (!match) return key;

    const [, prefix = "", suffix = ""] = match;

    const formattedPrefix = prefixMap[prefix.toLowerCase()] || prefix;
    const formattedSuffix = Object.entries(suffixMap).find(([rawSuffix]) =>
        suffix.endsWith(rawSuffix)
    )?.[1];

    if (formattedPrefix && formattedSuffix) {
        return `${formattedPrefix} ${formattedSuffix}`;
    }

    return key;
};

    return (
        <div className="flex flex-col xl:flex-row gap-10 mb-20">
            <div className="flex flex-col gap-8 xl:w-[calc(50%-20px)]">
                <div>
                    <h2 className="text-lg font-semibold text-secondary mb-2">
                        {t("searchAndSelectClubs")}
                    </h2>
                    <div className="flex items-center gap-4 mb-4 flex-wrap">
                        <input
                            type="text"
                            placeholder="Enter club name"
                            value={searchText}
                            onChange={(e) => {
                                setSearchState(false);
                                setSearchText(e.target.value);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg w-full max-w-sm"
                        />
                        <button
                            onClick={handleExplore}
                            className="px-8 py-2 bg-secondary text-white rounded-lg hover:bg-primary hover:text-secondary capitalize"
                        >
                            {t("Discover.form.lable3")}
                        </button>
                    </div>

                    {loadingClubs ? (
                        <div className="flex justify-center items-center">
                            <CgSpinner className="animate-spin text-[64px]" />
                        </div>
                    ) : clubs.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-2 gap-4 mt-8">
                            {clubs.map((club: any) => (
                                <div
                                    key={club?._id}
                                    className="flex flex-row items-center gap-4"
                                >
                                    <input
                                        id={`club-${club?._id}`}
                                        type="checkbox"
                                        checked={selectedClubs.some(
                                            (c: any) => c._id === club._id
                                        )}
                                        onChange={() =>
                                            toggleClub({
                                                _id: club._id,
                                                name: club.name
                                            })
                                        }
                                        className="block cursor-pointer"
                                    />
                                    <img
                                        id={`clubImage-${club?._id}`}
                                        src={
                                            club?.photo_url
                                                ? club?.photo_url
                                                : generateGameType(
                                                      club?.gameType ||
                                                          club?.name
                                                  )
                                        }
                                        className="w-[32px] h-[32px] object-contain border border-[#000b2966] rounded-[6px]"
                                        alt="Club Profile"
                                    />
                                    <label
                                        htmlFor={`club-${club?._id}`}
                                        className="text-[#00081e] flex flex-col"
                                    >
                                        <span
                                            id={`clubName-${club?._id}`}
                                            className="text-base font-medium hover:cursor-pointer"
                                        >
                                            {club?.name}
                                        </span>
                                        <span
                                            id={`clubCity-${club?._id}`}
                                            className="text-xs"
                                        >
                                            {club?.city}
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    ) : (
                        searchText &&
                        clubs?.length <= 0 &&
                        searchState && (
                            <p className="text-sm text-gray-500">
                                {t("noClubsFound")}
                            </p>
                        )
                    )}
                </div>

                {selectedClubs.length > 0 && (
                    <div>
                        <h3 className="text-lg font-semibold text-secondary mb-2">
                            {t("selectedClubs")}
                        </h3>
                        <ul className="list-disc list-inside text-gray-700 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                            {selectedClubs.map((club: any) => (
                                <li key={club._id}>{club.name}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div>
                    <h2 className="text-lg font-semibold text-secondary mb-2">
                        {t("selectStyle")}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                        {["tabs", "dropdown"].map((style: any) => (
                            <div
                                key={style}
                                className="flex items-center gap-2"
                            >
                                <input
                                    id={style}
                                    type="radio"
                                    name="style"
                                    value={style}
                                    checked={selectedStyle === style}
                                    onChange={() => setSelectedStyle(style)}
                                    className="cursor-pointer"
                                />
                                <label
                                    htmlFor={style}
                                    className="block cursor-pointer capitalize"
                                >
                                    {style}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <h2 className="text-lg font-semibold text-secondary mb-4">
                        {t("selectColors")}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                        {Object.entries(color).map(([key, value]) => {
                            let colorValue;
                            let hexMatch = value.match(/#([0-9a-fA-F]{6})/);
                            if (hexMatch) {
                                colorValue = `#${hexMatch[1]}`;
                            } else {
                                const aliasMatch = value.match(
                                    /(?:^|[-:])(?<name>primary|secondary)$/
                                );
                                if (aliasMatch?.groups?.name === "primary") {
                                    // return colorAliases[aliasMatch.groups.name];
                                    colorValue = "#43EC9B";
                                } else {
                                    colorValue = "#000B29";
                                }
                            }
                            return (
                                <div
                                    key={key}
                                    className="flex items-center gap-2"
                                >
                                    <label
                                        htmlFor={key}
                                        className="w-48 capitalize text-sm text-gray-700"
                                    >
                                        {/* {key} */}
                                        {formatColorLabel(key)}
                                    </label>
                                    <input
                                        id={key}
                                        type="color"
                                        value={colorValue}
                                        onChange={(e) =>
                                            handleColorChange(
                                                key,
                                                e.target.value
                                            )
                                        }
                                        className="cursor-pointer"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-center items-center mt-8">
                    <button
                        onClick={() => {
                            if (!generatedBlob) return;
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(generatedBlob);
                            link.download = "custom-widget.html";
                            link.click();
                        }}
                        className="px-4 py-2 rounded-lg bg-secondary text-white cursor-pointer hover:bg-primary hover:text-secondary transform duration-300 ease-in-out text-bold max-w-[300px] w-full font-bold"
                    >
                        {t("downloadHTML")}
                    </button>
                </div>
            </div>

            {generatedHTML && (
                <div className="w-full xl:w-[calc(50%-20px)]">
                    <h2 className="text-lg font-semibold text-secondary mb-2">
                        {t("generatedHTMLPreview")}
                    </h2>
                    <div
                        className="border border-secondary rounded-lg overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: generatedHTML }}
                    />
                </div>
            )}
        </div>
    );
};
